import { useState } from "react";

import { Button } from "@workspace/ui/components/button";
import { Label } from "@workspace/ui/components/label";
import { Textarea } from "@workspace/ui/components/textarea";

import type { EarlyLeaveReason } from "../types";
import { EARLY_LEAVE_NOTE_MAX, earlyLeaveReasons } from "./early-leave-reasons";

export type EarlyLeaveChoice = {
	reason: EarlyLeaveReason;
	note?: string;
};

/**
 * HA-368: three large tap tiles (Illness, Sent Home, Other). Other opens an optional note of up to 250 characters, typed on the
 * device's own on-screen keyboard. Confirm stays disabled until a reason is picked (AC7). A note goes only with Other.
 */
export function EarlyLeaveReasonPicker({
	onSubmit,
}: {
	onSubmit: (choice: EarlyLeaveChoice) => Promise<boolean>;
}) {
	const [reason, setReason] = useState<EarlyLeaveReason | null>(null);
	const [note, setNote] = useState("");
	const [submitting, setSubmitting] = useState(false);

	async function confirm() {
		if (!reason) return;

		setSubmitting(true);
		try {
			const recorded = await onSubmit(
				reason === "Other" && note.trim() ? { reason, note } : { reason },
			);
			if (recorded) {
				setReason(null);
				setNote("");
			}
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<div className="space-y-4 max-[400px]:space-y-3">
			<div
				role="group"
				aria-label="Reason for leaving early"
				className="grid grid-cols-3 gap-3 max-[400px]:gap-2"
			>
				{earlyLeaveReasons.map((option) => (
					<Button
						key={option.value}
						type="button"
						variant={reason === option.value ? "default" : "outline"}
						aria-pressed={reason === option.value}
						className="h-24 text-xl whitespace-normal max-[400px]:h-20 max-[400px]:text-lg"
						onClick={() => setReason(option.value)}
					>
						{option.label}
					</Button>
				))}
			</div>

			{reason === "Other" && (
				<div className="space-y-2 text-left">
					<Label htmlFor="early-leave-note" className="text-lg">
						Note (optional)
					</Label>
					<Textarea
						id="early-leave-note"
						value={note}
						maxLength={EARLY_LEAVE_NOTE_MAX}
						rows={3}
						className="text-lg"
						onChange={(e) => setNote(e.target.value)}
					/>
					<p className="text-sm text-muted-foreground text-right">
						{note.length}/{EARLY_LEAVE_NOTE_MAX}
					</p>
				</div>
			)}

			<Button
				type="button"
				className="w-full h-12 max-[400px]:h-10 text-lg max-[400px]:text-base"
				disabled={!reason || submitting}
				onClick={confirm}
			>
				Confirm
			</Button>
		</div>
	);
}
